import { useCallback, useLayoutEffect, useRef } from "react";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../types/application";

export function useNinjaEquifaxPanelPosition({
  applicationAllData,
  applicationData,
  showUpdateNinjaEquifaxScoresButton,
  scoresInputsHidden,
}: {
  applicationAllData: ApplicationDetailData | null;
  applicationData: ApplicationLayerData | null;
  showUpdateNinjaEquifaxScoresButton: boolean;
  scoresInputsHidden: boolean;
}) {
  const applicationAllDataRef = useRef(applicationAllData);

  const ninjaScoreTogglePrimaryRef = useRef<HTMLButtonElement | null>(null);
  const ninjaScoreToggleCoRef = useRef<HTMLButtonElement | null>(null);
  const ninjaPanelPrimaryRef = useRef<HTMLDivElement | null>(null);
  const ninjaPanelCoRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    applicationAllDataRef.current = applicationAllData;
  }, [applicationAllData]);

  const positionNinjaScorePanel = useCallback(() => {
    const data = applicationAllDataRef.current;
    const hasCoResidents =
      !!data?.co_resident_application &&
      Object.keys(data.co_resident_application).length > 0;
    const trigger = hasCoResidents
      ? ninjaScoreToggleCoRef.current
      : ninjaScoreTogglePrimaryRef.current;
    const panel = hasCoResidents
      ? ninjaPanelCoRef.current
      : ninjaPanelPrimaryRef.current;
    if (!trigger || !panel) return;

    const tr = trigger.getBoundingClientRect();
    const gap = 6;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let width = Math.min(640, Math.max(Math.round(tr.width), 500));
    width = Math.min(width, vw - 2 * margin);

    let left = Math.round(tr.left);
    if (left + width > vw - margin) {
      left = Math.max(margin, vw - margin - width);
    }
    if (left < margin) {
      left = margin;
      width = Math.min(width, vw - 2 * margin);
    }

    const top = Math.round(tr.bottom + gap);
    const maxH = Math.max(120, vh - top - margin);

    panel.style.position = "fixed";
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
    panel.style.width = `${width}px`;
    panel.style.maxHeight = `${maxH}px`;
    panel.style.overflowY = "auto";
    panel.style.zIndex = "9997";
  }, []);

  useLayoutEffect(() => {
    if (!showUpdateNinjaEquifaxScoresButton || scoresInputsHidden) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) positionNinjaScorePanel();
    };

    run();
    const raf0 = window.requestAnimationFrame(() => {
      if (cancelled) return;
      run();
      window.requestAnimationFrame(() => {
        if (!cancelled) run();
      });
    });

    window.addEventListener("resize", run);
    window.addEventListener("scroll", run, true);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf0);
      window.removeEventListener("resize", run);
      window.removeEventListener("scroll", run, true);
    };
  }, [
    scoresInputsHidden,
    showUpdateNinjaEquifaxScoresButton,
    applicationData,
    positionNinjaScorePanel,
  ]);

  return {
    ninjaScoreTogglePrimaryRef,
    ninjaScoreToggleCoRef,
    ninjaPanelPrimaryRef,
    ninjaPanelCoRef,
  };
}
